import "./DeleteSectionModal.scss";

import { FC, useState } from "react";

import { SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { showConfirmationModal } from "mds/components/Modal";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useAppSelector } from "store";
import { selectCourseCodeBySectionId } from "store/selectors/section";

export const showDeleteConfirmationModal = ({
  sectionId,
  sectionTitle,
  sectionKind,
}: {
  sectionId: string;
  sectionTitle: string;
  sectionKind: SectionKind;
}) => {
  const isTemplate = sectionKind === SectionKind.TEMPLATE;
  const deleteSectionTitle = isTemplate
    ? t("section_list_panel.delete_template_section_title")
    : t("confirm_delete_modal.delete_item", {
        item: t("glossary.section"),
      });
  const confirmButtonText = isTemplate
    ? t("section_list_panel.delete_template_section_confirm")
    : deleteSectionTitle;

  return showConfirmationModal({
    title: deleteSectionTitle,
    hideCancelConfirmButtons: true,
    labelText: t("confirm_delete_modal.delete_label", { name: sectionTitle }),

    children: (onClose, onConfirm) => (
      <DeleteSectionModal
        confirmButtonText={confirmButtonText}
        isTemplate={isTemplate}
        sectionId={sectionId}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    ),
  });
};

type DeleteSectionModalProps = {
  confirmButtonText: string;
  isTemplate: boolean;
  sectionId: string;
  onConfirm: (data?: boolean) => void;
  onClose: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
const DeleteSectionModal: FC<DeleteSectionModalProps> = ({
  sectionId,
  confirmButtonText,
  isTemplate,
  onConfirm,
  onClose,
}) => {
  const [disableDelete, setDisableDelete] = useState<boolean>(true);

  const courseTitle = useAppSelector((state) => selectCourseCodeBySectionId(state, sectionId));
  const handleCourseConfirmationCheck = (text: string) => {
    setDisableDelete(text !== courseTitle);
  };

  return (
    <div className="section-delete-modal mt-8 flex flex-col items-start gap-8">
      <div className="section-delete-warning">
        {isTemplate
          ? t("section_list_panel.delete_template_section_warning")
          : t("section_list_panel.delete_section_warning")}
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="course-code">{t("section_list_panel.course_confirm_label")}</label>

        <TextField
          id="course-code"
          placeholder={t("section_list_panel.course_placeholder")}
          isEditing
          onChange={(event) => handleCourseConfirmationCheck(event.target.value)}
        />
      </div>

      <div className="flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={disableDelete}
          kind="caution"
          size="m"
          title={confirmButtonText}
          onClick={() => onConfirm(true)}
        >
          {confirmButtonText}
        </SquareButton>
      </div>
    </div>
  );
};
